import { useMemo, useState, useCallback } from 'react'
import { useRouteLoaderData, useLoaderData, useParams, useNavigate } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from 'react-i18next'
import {
  GridColDef,
  GridInitialState,
  GridRowModesModel,
  GridEventListener,
  GridRowId,
  GridRowEditStopReasons,
  GridRowModes,
  GridActionsCellItem,
} from '@mui/x-data-grid-premium'
import { toast } from 'react-toastify'

import { LoaderData, Abstracts, AbstractRolesType } from '../../../types'
import {
  useDomainsDataOptions,
  useThemesDataOptions,
  useThemeCategoryDataOptions,
  useAbstractStatusOptions,
  useAbstractSelectionStatusOptions,
} from '../../../utils'
import { useModifySelectionAbstractMutation } from '../../../services/events'
import { adminEventDetailsLoader, adminAbstractsLoader } from '../Admin.loader'
import AbstractStatusTag from '../../abstracts/components/AbstractStatusTag'
import AbstractSelectionStatusTag from '../../abstracts/components/AbstractSelectionStatusTag'
import AbstractDetails from '../../abstracts/components/AbstractDetails'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import { useContextShowStatistics } from '../Admin.Context'
import AdminAbstractsDetails from './components/AdminAbstractsDetails'
import ViewButtonGroup from './components/ViewButtonGroup'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '70rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

type Row = Abstracts<true>

const initialState: GridInitialState = {
  sorting: {
    sortModel: [
      {
        field: 'sub_event_type',
        sort: 'desc',
      },
    ],
  },
  columns: {
    columnVisibilityModel: {
      status: false,
    },
  },
}

export default function AdminAbstracts() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { eventId } = useParams()
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')

  const showStatistics = useContextShowStatistics()
  const [open, setOpen] = useState(-1)
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  const { event, sessions } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >
  const { abstracts, statistics } = useLoaderData() as LoaderData<
    ReturnType<typeof adminAbstractsLoader>
  >
  const data = abstracts?.results satisfies Row[] | undefined

  const [modifySelectionAbstract] = useModifySelectionAbstractMutation()

  const statusOptions = useAbstractStatusOptions(t)
  const themesDataOptions = useThemesDataOptions()
  const themeCategoryOptions = useThemeCategoryDataOptions(t)
  const domainsDataOptions = useDomainsDataOptions()
  const abstractSelectionStatusOptions = useAbstractSelectionStatusOptions(t)

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
    },
    [rowModesModel]
  )

  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      })
    },
    [rowModesModel]
  )

  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
    },
    [rowModesModel]
  )

  const handleOpenPreview = (id: number) => () => {
    setOpen(id)
  }

  const processRowUpdate = async (newRow: Row, oldRow: Row) => {
    let updatedRow = oldRow
    await modifySelectionAbstract({
      eventId: Number(eventId),
      id: newRow.id,
      data: {
        selection_status: newRow.selection_status,
        session: newRow.session,
        order: newRow.order,
      },
    })
      .then(({ data }) => {
        if (data) {
          updatedRow = data
        }
        // toast.success(t('Les modifications ont été enregistrées'))
      })
      .catch(e => {
        toast.error(
          t("Une erreur s'est produite lors de l'enregistrement des modifications") +
            '.' +
            t('Certaines données peuvent ne pas avoir été enregistrées')
        )
        console.error(e)
      })
    return updatedRow
  }

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit

          if (isInEditMode) {
            return [
              <Tooltip title={t('Save')}>
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label="Save"
                  sx={{
                    color: 'primary.main',
                  }}
                  onClick={handleSaveClick(params.id)}
                />
              </Tooltip>,
              <Tooltip title={t('Cancel')}>
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label="Cancel"
                  onClick={handleCancelClick(params.id)}
                />
              </Tooltip>,
            ]
          }

          return [
            <Tooltip title={t('Voir')}>
              <GridActionsCellItem
                label="Voir"
                icon={<VisibilityIcon />}
                onClick={handleOpenPreview(params.id as number)}
                sx={{
                  color: 'primary.main',
                }}
              />
            </Tooltip>,
            <Tooltip title={t('Éditer')}>
              <GridActionsCellItem
                label="Éditer"
                icon={<EditIcon />}
                onClick={handleEditClick(params.id)}
              />
            </Tooltip>,
          ]
        },
      },
      { field: 'id', headerName: t('ID'), width: 60 },
      {
        field: 'number_of_ratings',
        type: 'number',
        headerName: t('Nb éval'),
        width: 90,
      },
      {
        field: 'rating_average',
        headerName: t('Note moy.'),
        type: 'number',
        width: 90,
      },
      {
        field: 'rating_stddev',
        headerName: t('Écart type'),
        type: 'number',
        width: 90,
      },
      {
        field: 'selection_status',
        headerName: t('Statut de sélection'),
        width: 180,
        editable: true,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: abstractSelectionStatusOptions,
        renderCell: params => (
          <AbstractSelectionStatusTag
            size="small"
            status={params.value as Row['selection_status']}
          />
        ),
      },
      {
        field: 'session',
        headerName: t('Séance'),
        width: 200,
        editable: false,
        type: 'singleSelect',
        valueOptions: sessions?.map(session => ({
          ...session,
          value: session.id,
          label: session.name,
        })),
      },
      {
        field: 'order',
        headerName: t('Ordre'),
        width: 80,
        type: 'number',
      },
      {
        field: 'title',
        headerName: t('Titre du résumé'),
        width: 270,
      },
      {
        field: 'status',
        headerName: t('Statut du résumé'),
        width: 150,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: statusOptions,
        renderCell: params => (
          <AbstractStatusTag size="small" status={params.value as Row['status']} />
        ),
      },
      {
        field: 'category',
        headerName: t('Catégorie'),
        width: 150,
        type: 'singleSelect',
        valueOptions: themeCategoryOptions,
      },
      {
        field: 'domain',
        headerName: t('Domaine'),
        width: 150,
        type: 'singleSelect',
        valueOptions: domainsDataOptions,
      },
      {
        field: 'theme',
        headerName: t('Thème'),
        width: 250,
        type: 'singleSelect',
        valueOptions: themesDataOptions,
      },
      {
        field: 'speakers',
        headerName: t('Orateurs'),
        width: 200,
        valueGetter: (_value, params) =>
          params.authors
            .filter(author => author.roles.includes(AbstractRolesType.Speaker))
            .map(author => `${author.user.first_name} ${author.user.last_name}`)
            .join(', '),
      },
      {
        field: 'authors',
        headerName: t('Auteurs'),
        width: 200,
        valueGetter: (_value, params) =>
          params.authors
            .filter(author => author.roles.includes(AbstractRolesType.Author))
            .map(author => `${author.user.first_name} ${author.user.last_name}`)
            .join(', '),
      },
      {
        field: 'coauthors',
        headerName: t('Co-auteurs'),
        width: 200,
        valueGetter: (_value, params) =>
          params.authors
            .filter(author => author.roles.includes(AbstractRolesType.CoAuthor))
            .map(author => `${author.user.first_name} ${author.user.last_name}`)
            .join(', '),
      },
    ],
    [
      t,
      abstractSelectionStatusOptions,
      sessions,
      statusOptions,
      themeCategoryOptions,
      domainsDataOptions,
      themesDataOptions,
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
    ]
  )

  return (
    <Stack
      sx={theme => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.boxShadows.main,
      })}
      height="inherit"
      overflow="hidden auto"
      padding="0.5rem"
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">{t('Détails des résumés')}</Typography>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && <AdminAbstractsDetails statistics={statistics} />}

      <Divider />
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ margin: '1rem 0' }}
        >
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <h3>{t('Liste des résumés')} :</h3>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate('sessions')
              }}
            >
              {t('Construction')}
            </Button>
          </Stack>

          <ViewButtonGroup />
        </Stack>
        <Stack
          sx={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
            overflow: 'auto',
          }}
        >
          <StripedDataGrid
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            rows={data}
            columns={columns}
            initialState={initialState}
            count={abstracts?.count}
            paginationMode="server"
            editOptions={{
              onClick: selectedRows => {
                const newRowModelProps: GridRowModesModel = [...selectedRows].reduce(
                  (acc, [rowId]) => {
                    acc[rowId] = { mode: GridRowModes.Edit }
                    return acc
                  },
                  {} as GridRowModesModel
                )
                setRowModesModel({
                  ...rowModesModel,
                  ...newRowModelProps,
                })
              },
            }}
            saveAllOptions={{
              onClick() {
                if (Object.values(rowModesModel).some(row => row.mode === GridRowModes.Edit)) {
                  const newRowModelProps: GridRowModesModel = Object.keys(rowModesModel).reduce(
                    (acc, rowId) => {
                      if (rowModesModel[rowId].mode === GridRowModes.Edit) {
                        acc[rowId] = { mode: GridRowModes.View }
                      }
                      return acc
                    },
                    {} as GridRowModesModel
                  )
                  setRowModesModel(newRowModelProps)
                } else {
                  toast.info(t('Aucune modification à enregistrer'))
                }
              },
            }}
          />
        </Stack>
      </Stack>

      <Modal
        open={open !== -1}
        onClose={() => {
          setOpen(-1)
        }}
      >
        <Stack sx={style} gap="0.5rem">
          <AbstractDetails abstract={data?.find(el => el.id === open)} />
        </Stack>
      </Modal>
    </Stack>
  )
}
